/**
 * src/hooks/useFileDrop.ts — window-level file drag-drop (W5 web ergonomics).
 *
 * Web-only. Lets the user drop receipts/documents anywhere on the capture/import screens. Uses a
 * window-level listener (no DOM ref needed, portable across react-native-web). No-op on native and
 * when disabled. The latest callback is held in a ref so the listener doesn't churn each render.
 *
 * NEVER imports @smartbox/* (mobile invariant 1).
 */
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { fileToAcquired, type AcquiredFile } from '../platform/fileCapture';

export function useFileDrop(onFiles: (files: AcquiredFile[]) => void, enabled = true): void {
  const cbRef = useRef(onFiles);
  cbRef.current = onFiles;

  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled || typeof window === 'undefined') return;

    const prevent = (e: DragEvent) => e.preventDefault();
    const onDrop = async (e: DragEvent) => {
      e.preventDefault();
      const list = e.dataTransfer?.files;
      if (!list || list.length === 0) return;
      try {
        const acquired = await Promise.all(Array.from(list).map((f) => fileToAcquired(f)));
        cbRef.current(acquired);
      } catch {
        // ignore unreadable drops
      }
    };

    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', onDrop);
    };
  }, [enabled]);
}
